import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useRouteMatch } from 'react-router-dom'

import { IS_EMBEDDED } from '@sketch/constants'
import { shortcuts } from '@sketch/utils'
import {
  Navbar,
  useForTablet,
  IsEmbedded,
  NotEmbedded,
  ModalRestrictor,
  MenuIconButton,
  TooltipShortcut,
} from '@sketch/components'
import {
  RouteParams,
  useCopyLink,
  useFlag,
  useUserSignedIn,
} from '@sketch/modules-common'
import { ShareWithoutVersion } from '../../../../versioning'
import { useHasNewNotifications } from '../../../../annotations/hooks'

import HeaderNavigation from '../../../components/HeaderNavigation'
import DocumentNavbar from '../../../components/DocumentNavbar'
import DocumentSettingsDropdown from '../../../components/DocumentHeader/DocumentSettingsDropdown'
import DerivedHeaderActions from '../../../components/DerivedHeaderActions'
import { PartialShareDropdown } from '../../../components/PartialShareDropdown'

import { ActionButton } from './PageCanvasViewHeader.styles'

import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'
import { VersionStatusButton } from '../../../components/DocumentHeader/VersionStatusButton'
import { useShareModal } from './PageCanvasViewHeader.hooks'

import { NavbarItem, VerticalDivider } from '../../../components/NavbarItem'
import {
  useShareSidebarTab,
  getActivePanels,
  SegmentValues,
} from '../../../components/ShareSidebarTabContext'
import { PreviewHeader } from './PreviewHeader'

import { ReactComponent as ClockReWind } from '@sketch/icons/clock-rewind-24'
import { ReactComponent as ChatBubbles } from '@sketch/icons/chat-bubbles-24'
import { ReactComponent as CodeBlock } from '@sketch/icons/code-block-24'
import { ReactComponent as Information } from '@sketch/icons/information-24'

import { VersionFragment } from '@sketch/gql-types'
import {
  useIsPreviewView,
  usePreview,
} from '../../../views/PreviewView/PreviewContext'

type PageCanvasViewHeaderProps = {
  share?: ShareWithoutVersion
  isViewingLatestVersion: boolean
  isViewingStarredVersion: boolean
  hasPendingPatches: boolean
  userCanOpenInApp: boolean
  currentVersion?: VersionFragment
  hideSearch?: boolean
} & Pick<
  DocumentSidebarLayoutExtraProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

interface CommentsMenuItemProps {
  activeSegment?: SegmentValues
  toggleSegment: (hash: SegmentValues) => void
  userIsMember?: boolean
}

const CommentsMenuItem: React.FC<CommentsMenuItemProps> = props => {
  const { userIsMember } = props

  const hasNewNotifications = useHasNewNotifications({
    skip: !userIsMember,
  })

  return (
    <MenuIconButton
      description={
        <>
          Comments{' '}
          <TooltipShortcut>
            Press {shortcuts.comment.toUpperCase()}
          </TooltipShortcut>
        </>
      }
      data-testid="comment-tab"
      aria-current={props.activeSegment === 'Comment'}
      onClick={() => props.toggleSegment('Comment')}
      $isHighlighted={hasNewNotifications}
    >
      <ChatBubbles role="img" />
      <span className="sr-only">Comments</span>
    </MenuIconButton>
  )
}

const FloatingPanelTriggers = (props: PageCanvasViewHeaderProps) => {
  const {
    share,
    isViewingLatestVersion,
    userCanOpenInApp,
    isViewingStarredVersion,
    hasPendingPatches,
    isSidebarRightOpen,
    toggleSidebarRight,
    currentVersion,
  } = props

  const isTabletAndBigger = useForTablet()
  const showShareModal = useShareModal()
  const previewContext = usePreview()
  const { pageUUID } = useParams<RouteParams<'SHARE_PAGE_CANVAS_VIEW'>>()
  const { path } = useRouteMatch()
  const isPartialSharingEnabled = useFlag('partial-sharing')
  const isSignedIn = useUserSignedIn()

  const canInspect = share
    ? share.userCanInspect
    : !!previewContext?.preview.inspectEnabled

  const activePanels = useMemo(
    () =>
      getActivePanels({
        path,
        canInspect,
        // Even when the comments are not allowed, the comments panel should be available
        canComment: true,
        isSignedIn,
      }),
    [path, canInspect, isSignedIn]
  )

  /**
   * Save the tabs the user has access and
   * update if "shouldShowCommentTab" and "shouldShowInspector" updates
   */
  const [segments, setSegments] = useState(activePanels)
  useEffect(() => {
    setSegments(activePanels)
  }, [activePanels])

  const { activeSegment, toggleSegment } = useShareSidebarTab(segments, {
    isSidebarRightOpen,
    toggleSidebarRight,
  })

  const copyLink = useCopyLink()

  const showLatestVersionButton = isTabletAndBigger && !IS_EMBEDDED

  const shareButton =
    share && isPartialSharingEnabled ? (
      <PartialShareDropdown share={share} />
    ) : (
      <ActionButton onClick={showShareModal}>Share...</ActionButton>
    )

  const canSeeComments = !previewContext || (previewContext && isSignedIn)

  return (
    <>
      {share && (
        <DerivedHeaderActions
          share={share}
          currentVersion={currentVersion || null}
          userCanOpenInApp={userCanOpenInApp}
          isViewingLatestVersion={isViewingLatestVersion}
        />
      )}

      {isTabletAndBigger && (
        <NavbarItem>
          {share && share.userCanUpdateSettings ? (
            shareButton
          ) : (
            <ActionButton onClick={copyLink}>Copy Link</ActionButton>
          )}
        </NavbarItem>
      )}

      {share && showLatestVersionButton && (
        <VersionStatusButton
          shareID={share.identifier}
          pageUUID={pageUUID}
          isViewingLatestVersion={isViewingLatestVersion}
          isViewingStarredVersion={isViewingStarredVersion}
          userCanEdit={userCanOpenInApp}
          hasPendingPatches={hasPendingPatches}
        />
      )}

      {share && (
        <NavbarItem>
          <ModalRestrictor>
            <DocumentSettingsDropdown
              showAnnotationActions
              pinDocumentConfig={{
                shareIdentifier: share.identifier,
                pinned: !!share?.pinnedByCurrentUserAt,
              }}
            />
          </ModalRestrictor>
        </NavbarItem>
      )}

      {isTabletAndBigger && (
        <>
          {(share || canInspect || canSeeComments) && <VerticalDivider />}

          <NavbarItem data-testid="panel-triggers-header">
            {share && (
              <MenuIconButton
                description={
                  <>
                    Document details{' '}
                    <TooltipShortcut>
                      Press {shortcuts.about.toUpperCase()}
                    </TooltipShortcut>
                  </>
                }
                aria-current={activeSegment === 'About'}
                onClick={() => toggleSegment('About')}
              >
                <Information role="img" />
                <span className="sr-only">About</span>
              </MenuIconButton>
            )}
            {previewContext && (
              <MenuIconButton
                description={
                  <>
                    Preview details{' '}
                    <TooltipShortcut>
                      Press {shortcuts.about.toUpperCase()}
                    </TooltipShortcut>
                  </>
                }
                aria-current={activeSegment === 'About'}
                onClick={() => toggleSegment('About')}
              >
                <Information role="img" />
                <span className="sr-only">About</span>
              </MenuIconButton>
            )}
            {share && (
              <MenuIconButton
                description={
                  <>
                    Versions{' '}
                    <TooltipShortcut>
                      Press {shortcuts.version.toUpperCase()}
                    </TooltipShortcut>
                  </>
                }
                aria-current={activeSegment === 'Version'}
                onClick={() => toggleSegment('Version')}
              >
                <ClockReWind role="img" />
                <span className="sr-only">Versions</span>
              </MenuIconButton>
            )}
            {canInspect && (
              <MenuIconButton
                description={
                  <>
                    Inspect{' '}
                    <TooltipShortcut>
                      Press {shortcuts.inspect.toUpperCase()}
                    </TooltipShortcut>
                  </>
                }
                aria-current={activeSegment === 'Inspect'}
                onClick={() => toggleSegment('Inspect')}
              >
                <CodeBlock role="img" />
                <span className="sr-only">Inspect</span>
              </MenuIconButton>
            )}
            {canSeeComments && (
              <CommentsMenuItem
                userIsMember={share?.workspace?.userIsMember}
                activeSegment={activeSegment}
                toggleSegment={toggleSegment}
              />
            )}
          </NavbarItem>
        </>
      )}
    </>
  )
}

export const PageCanvasViewHeader: React.FC<
  PageCanvasViewHeaderProps
> = props => {
  const isTabletAndBigger = useForTablet()
  const isPreview = useIsPreviewView()

  return (
    <>
      <NotEmbedded>
        <Navbar.Section>
          {props.share ? (
            <HeaderNavigation share={props.share} />
          ) : (
            <PreviewHeader />
          )}
        </Navbar.Section>

        <Navbar.FixedWidthSection align="end">
          {isTabletAndBigger && !isPreview && (
            <>
              <NavbarItem>
                <DocumentNavbar hidden={props.hideSearch} />
              </NavbarItem>
            </>
          )}

          <FloatingPanelTriggers {...props} />
        </Navbar.FixedWidthSection>
      </NotEmbedded>

      <IsEmbedded>
        <Navbar.Section>
          {props.share ? (
            <HeaderNavigation share={props.share} />
          ) : (
            <PreviewHeader />
          )}
        </Navbar.Section>
      </IsEmbedded>
    </>
  )
}

export default PageCanvasViewHeader
